<template>
    <div class="wf-field">
        <label class="wf-label">
            {{ label }} <span v-if="isRequired" class="wf-required">*</span>
        </label>
        <p v-if="instruction" class="wf-instruction">{{ instruction }}</p>
        <div
            class="wf-upload-zone"
            :class="{ 'wf-upload-zone--error': error, 'wf-upload-zone--dragging': dragging }"
            @dragover.prevent="dragging = true"
            @dragleave.prevent="dragging = false"
            @drop.prevent="onDrop"
        >
            <input
                ref="fileInput"
                type="file"
                multiple
                :accept="acceptedTypes"
                class="wf-upload-input"
                @change="onFileChange"
            />
            <div class="wf-upload-placeholder" @click="$refs.fileInput.click()">
                <i class="fal fa-cloud-upload"></i>
                <span>{{ $t('wizard.upload_click') }}</span>
                <span v-if="maxFiles" class="wf-upload-count-hint">
                    {{ files.length + existingFiles.length }} / {{ maxFiles }}
                </span>
            </div>
        </div>
        <ul v-if="uploadErrors.length" class="wf-upload-errors">
            <li v-for="(msg, i) in uploadErrors" :key="i">
                <i class="fal fa-exclamation-triangle"></i> {{ msg }}
            </li>
        </ul>
        <!-- Existing files from server -->
        <div v-if="existingFiles.length || files.length" class="wfu-previews">
            <div v-for="(ef, i) in existingFiles" :key="'existing-' + ef.id" class="wfu-card">
                <div v-if="isImageName(ef.name)" class="wfu-card-img">
                    <img :src="ef.url" :alt="ef.name" />
                </div>
                <div v-else-if="isVideoName(ef.name)" class="wfu-card-video">
                    <video :src="ef.url" controls></video>
                </div>
                <div v-else class="wfu-card-icon">
                    <i :class="fileIcon(ef.name)"></i>
                </div>
                <button type="button" class="wfu-card-remove" @click="removeExistingFile(i)">
                    <i class="fal fa-times"></i>
                </button>
                <div class="wfu-card-footer">
                    <span class="wfu-card-name">{{ ef.name }}</span>
                </div>
            </div>
            <!-- New files -->
            <div v-for="(file, i) in files" :key="'new-' + i" class="wfu-card">
                <div v-if="isImage(file)" class="wfu-card-img">
                    <img :src="previews[i]" :alt="file.name" />
                </div>
                <div v-else-if="isVideo(file)" class="wfu-card-video">
                    <video :src="previews[i]" controls></video>
                </div>
                <div v-else class="wfu-card-icon">
                    <i :class="fileIcon(file.name)"></i>
                </div>
                <button type="button" class="wfu-card-remove" @click="removeFile(i)">
                    <i class="fal fa-times"></i>
                </button>
                <div class="wfu-card-footer">
                    <span class="wfu-card-name">{{ file.name }}</span>
                </div>
            </div>
        </div>
        <p v-if="help" class="wf-help">{{ help }}</p>
    </div>
</template>

<script>
import FieldMixin from './FieldMixin'

const DEFAULT_MAX_SIZE_MB = 6

export default {
    name: 'FileUploadField',
    mixins: [FieldMixin],
    data() {
        return {
            files: [],
            previews: [],
            encodedFiles: [],
            existingFiles: [],
            dragging: false,
            uploadErrors: []
        }
    },
    computed: {
        maxSizeMb() {
            const configured = this.attribute.config && this.attribute.config.max
            return configured > 0 ? configured : DEFAULT_MAX_SIZE_MB
        },
        maxFiles() {
            const configured = this.attribute.config && this.attribute.config.max_files
            return configured > 0 ? configured : null
        },
        maxSizeBytes() {
            return this.maxSizeMb * 1024 * 1024
        },
        acceptedExts() {
            if (!this.attribute.config) return null
            const ext = this.attribute.config['accepted-ext']
            if (ext && Array.isArray(ext) && ext.length) {
                return ext.map((e) => e.trim().toLowerCase().replace(/^\./, ''))
            }
            return null
        },
        acceptedTypes() {
            if (!this.acceptedExts) return '*'
            return this.acceptedExts.map((e) => '.' + e).join(',')
        }
    },
    created() {
        this.loadExistingFiles()
    },
    methods: {
        loadExistingFiles() {
            if (!this.value || !Array.isArray(this.value)) return
            const baseUrl = this.$store.state.baseURL
            const token = this.$store.state.auth.token
            const post = this.$store.state.post.post
            const medias = (post && post.medias) || []

            this.value.forEach((mediaId) => {
                const media = medias.find((m) => m.id == mediaId)
                if (media) {
                    this.existingFiles.push({
                        id: media.id,
                        name: media.name,
                        url: `${baseUrl}/storage/${media.id}?token=${token}`
                    })
                }
            })
        },
        emitAll() {
            const existing = this.existingFiles.map((ef) => ({ id: ef.id, existing: true }))
            const newFiles = this.encodedFiles
            const combined = [...existing, ...newFiles]
            this.emitValue(combined.length ? combined : null)
        },
        removeExistingFile(index) {
            this.existingFiles.splice(index, 1)
            this.emitAll()
        },
        onFileChange(e) {
            this.addFiles(Array.from(e.target.files))
        },
        onDrop(e) {
            this.dragging = false
            this.addFiles(Array.from(e.dataTransfer.files))
        },
        addFiles(newFiles) {
            this.uploadErrors = []
            const currentCount = this.existingFiles.length + this.files.length
            if (this.maxFiles !== null && currentCount >= this.maxFiles) {
                this.uploadErrors.push(this.$t('wizard.upload_error_max_files', { max: this.maxFiles }))
                return
            }
            const filesToProcess = this.maxFiles !== null
                ? newFiles.slice(0, this.maxFiles - currentCount)
                : newFiles
            const accepted = []
            for (const file of filesToProcess) {
                const reason = this.validateFile(file)
                if (reason) {
                    this.uploadErrors.push(reason)
                    continue
                }
                accepted.push(file)
            }
            accepted.forEach((file) => {
                this.files.push(file)
                if (this.isImage(file) || this.isVideo(file)) {
                    const url = URL.createObjectURL(file)
                    this.previews.push(url)
                } else {
                    this.previews.push(null)
                }
                this.encodeFile(file)
            })
            if (this.$refs.fileInput) this.$refs.fileInput.value = ''
        },
        validateFile(file) {
            if (file.size > this.maxSizeBytes) {
                return this.$t('wizard.upload_error_size', {
                    name: file.name,
                    max: this.maxSizeMb
                })
            }
            if (this.acceptedExts) {
                const ext = (file.name.split('.').pop() || '').toLowerCase()
                if (!this.acceptedExts.includes(ext)) {
                    return this.$t('wizard.upload_error_ext', {
                        name: file.name,
                        exts: this.acceptedExts.join(', ')
                    })
                }
            }
            return null
        },
        encodeFile(file) {
            const reader = new FileReader()
            reader.onloadend = () => {
                this.encodedFiles.push({
                    name: file.name,
                    encodedFile: reader.result
                })
                this.emitAll()
            }
            reader.readAsDataURL(file)
        },
        removeFile(index) {
            if (this.previews[index]) URL.revokeObjectURL(this.previews[index])
            this.files.splice(index, 1)
            this.previews.splice(index, 1)
            this.encodedFiles.splice(index, 1)
            this.emitAll()
        },
        isImage(file) {
            if (!file || !file.name) return false
            return this.isImageName(file.name)
        },
        isVideo(file) {
            if (!file || !file.name) return false
            return this.isVideoName(file.name)
        },
        isImageName(name) {
            if (!name) return false
            const ext = name.split('.').pop().toLowerCase()
            return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
        },
        isVideoName(name) {
            if (!name) return false
            const ext = name.split('.').pop().toLowerCase()
            return ['mp4', 'avi', 'mov', 'mpg', 'mpeg', 'webm'].includes(ext)
        },
        fileIcon(name) {
            if (!name) return 'fal fa-file'
            const ext = name.split('.').pop().toLowerCase()
            if (['pdf'].includes(ext)) return 'fal fa-file-pdf'
            if (['doc', 'docx'].includes(ext)) return 'fal fa-file-word'
            if (['xls', 'xlsx'].includes(ext)) return 'fal fa-file-excel'
            if (['mp4', 'avi', 'mov'].includes(ext)) return 'fal fa-file-video'
            if (['mp3', 'wav', 'ogg'].includes(ext)) return 'fal fa-file-audio'
            return 'fal fa-file'
        }
    }
}
</script>

<style scoped>
.wfu-previews {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 10px;
    margin-top: 12px;
}

.wfu-card {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fff;
    transition: border-color 0.2s;
    position: relative;
}

.wfu-card:hover {
    border-color: #bfdbfe;
}

.wfu-card-img {
    height: 150px;
    overflow: hidden;
    background: #f8fafc;
    border-radius: 8px 8px 0 0;
}

.wfu-card-img img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.wfu-card-video {
    height: 150px;
    overflow: hidden;
    background: #000;
    border-radius: 8px 8px 0 0;
}

.wfu-card-video video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
}

.wfu-card-icon {
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8fafc;
    font-size: 1.2rem;
    color: #94a3b8;
}

.wfu-card-footer {
    padding: 4px 6px;
    border-top: 1px solid #f1f5f9;
}

.wfu-card-name {
    display: block;
    font-size: 0.55rem;
    color: #64748b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.wfu-card-remove {
    position: absolute;
    top: 5px;
    right: 5px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: none;
    background: rgba(239, 68, 68, 0.85);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 0.55rem;
    transition: background 0.2s;
}

.wfu-card-remove:hover {
    background: #dc2626;
}

.wf-upload-errors {
    list-style: none;
    margin: 8px 0 0;
    padding: 0;
}

.wf-upload-errors li {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    padding: 6px 10px;
    margin-top: 4px;
    background: rgba(239, 68, 68, 0.08);
    border: 1px solid rgba(239, 68, 68, 0.25);
    border-radius: 6px;
    color: #b91c1c;
    font-size: 0.8rem;
}

.wf-upload-errors li i {
    flex-shrink: 0;
    margin-top: 2px;
}
</style>
